import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setArticles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div data-testid="blog-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <div className="overline mb-4">Blog & Insights</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#0A1128] mb-6">Stories, guides &<br/> <span className="italic text-[#688A4E]">helpful advice</span>.</h1>
        <p className="text-lg text-gray-600 max-w-2xl">MOM updates, Filipino helper hiring guides, family stories, and expert tips — from Singapore's trusted domestic helper agency.</p>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-24 text-gray-500">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-24 text-center border border-gray-200 py-24 mx-auto" data-testid="blog-empty">
          <div className="font-serif text-2xl text-[#0A1128] mb-3">Fresh articles coming soon.</div>
          <p className="text-gray-600 mb-8">Our editors are curating expert content on hiring, care and family life.</p>
          <Link to="/contact" className="btn-outline">Get in touch instead</Link>
        </div>
      ) : (
        <>
          {featured && (
            <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
              <Link to={`/blog/${featured.slug}`} data-testid={`featured-${featured.slug}`} className="grid lg:grid-cols-12 gap-8 group">
                <div className="lg:col-span-7 overflow-hidden">
                  {featured.hero_image ? (
                    <img src={resolveMediaUrl(featured.hero_image)} alt={featured.title} className="w-full h-[440px] object-cover group-hover:scale-105 transition duration-700"/>
                  ) : (
                    <div className="w-full h-[440px] bg-[#E8ECE1] flex items-center justify-center text-[#688A4E] font-serif text-2xl">Happy Home Agency</div>
                  )}
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="overline mb-4">Featured</div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(featured.tags || []).slice(0, 3).map((t) => <span key={t} className="text-xs border border-gray-300 px-2 py-1">{t}</span>)}
                  </div>
                  <h2 className="font-serif text-4xl sm:text-5xl text-[#0A1128] leading-tight mb-4 group-hover:text-[#688A4E] transition">{featured.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">{featured.excerpt}</p>
                  <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-gray-500 mb-6">
                    <Calendar className="w-3.5 h-3.5"/> {new Date(featured.published_at || featured.updated_at).toLocaleDateString("en-SG", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                  <div className="flex items-center gap-2 text-[#688A4E] text-sm">Read article <ArrowRight className="w-4 h-4"/></div>
                </div>
              </Link>
            </section>
          )}

          {rest.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-32">
              <div className="border-t border-gray-200 pt-16 mb-10">
                <div className="overline">More Articles</div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14">
                {rest.map((a) => (
                  <Link to={`/blog/${a.slug}`} key={a.id} data-testid={`article-card-${a.slug}`} className="group">
                    <div className="overflow-hidden mb-5">
                      {a.hero_image ? (
                        <img src={resolveMediaUrl(a.hero_image)} alt={a.title} className="w-full h-56 object-cover group-hover:scale-105 transition duration-700"/>
                      ) : (
                        <div className="w-full h-56 bg-[#E8ECE1] flex items-center justify-center text-[#688A4E] font-serif">Happy Home</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(a.tags || []).slice(0, 2).map((t) => <span key={t} className="text-[10px] border border-gray-300 px-2 py-0.5">{t}</span>)}
                    </div>
                    <h3 className="font-serif text-2xl text-[#0A1128] leading-tight mb-3 group-hover:text-[#688A4E] transition">{a.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-3">{a.excerpt}</p>
                    <div className="text-xs tracking-widest uppercase text-gray-500">{new Date(a.published_at || a.updated_at).toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" })}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
